import {
  BETWEEN,
  GREATER_THAN,
  LESS_THAN,
  TEXT_CONTAINING,
  TEXT_EQUAL_TO,
  TEXT_NOT_CONTAINING,
  TEXT_NOT_EQUAL_TO,
} from "../../utils/constants/constants";

/**
 * @param formatOverrides {object[]}
 * @param dataRow {object}
 * @param subKey {string} The property of the object that has the value
 * @param formatsByKey {Record<string, string>}
 * @returns {*}
 */
export function doOverride(formatOverrides, dataRow, subKey, formatsByKey) {
  return (
    tryFindOverride(formatOverrides, dataRow, subKey) || formatsByKey[subKey]
  );
}

/**
 * @param formatOverrides {object[]}
 * @param dataRow {object}
 * @param subKey {string} The property of the object that has the value
 * @returns {*}
 */
export function tryFindOverride(formatOverrides, dataRow, subKey) {
  return formatOverrides.find((formatOverride) => {
    const { conditionKey, conditionValue, overrideKey, overrideFormat } =
      formatOverride;

    function shouldUseOverride() {
      const matchedValue =
        (dataRow[conditionKey] + "")?.trim() === conditionValue;
      const configuredToMatch = subKey === overrideKey;
      return matchedValue && configuredToMatch;
    }

    return shouldUseOverride() ? overrideFormat : null;
  });
}

/**
 * Determines the color to apply to a table cell based on a conditional formatting rule (edge).
 *
 * - Supports numeric and text-based comparisons.
 * - Can evaluate either the current row value or a value from a dynamic column.
 * - Returns a success or error color from the provided theme.
 *
 * @param {Object} edge - The conditional formatting rule, including operator, value(s), and optionally dynamicColumnName.
 * @param {*} rowValue - The current value of the cell.
 * @param {Object} theme - Theme object containing notification color tokens.
 * @param {Object} sourceData - The full row object, used if dynamicColumnName is specified.
 * @returns {string|undefined} The color to apply (successMain as green or errorMain as red), or undefined if no match.
 */
export function getColoredColumnEdgesByOperator(
  edge,
  rowValue,
  theme,
  sourceData
) {
  if (!edge) {
    return;
  }

  const { operator = GREATER_THAN, min, max, reverse } = edge;

  function getTargetValue(edge) {
    const { dynamicColumnName, dynamicColumnValue, columnName, value } = edge;

    if (!dynamicColumnName) return value;

    const targetRow = sourceData?.find(
      (row) => row[dynamicColumnName] === dynamicColumnValue
    );

    return targetRow?.[columnName];
  }

  const value = getTargetValue(edge);

  switch (operator) {
    case GREATER_THAN: {
      if (+rowValue > +value) {
        return reverse
          ? theme.notification.errorMain
          : theme.notification.successMain;
      } else if (+rowValue < +value) {
        return reverse
          ? theme.notification.successMain
          : theme.notification.errorMain;
      }
      break;
    }

    case LESS_THAN: {
      if (+rowValue < +value) {
        return reverse
          ? theme.notification.errorMain
          : theme.notification.successMain;
      } else if (+rowValue > +value) {
        return reverse
          ? theme.notification.successMain
          : theme.notification.errorMain;
      }
      break;
    }

    case BETWEEN: {
      if (+rowValue > +min && +rowValue < +max) {
        return reverse
          ? theme.notification.errorMain
          : theme.notification.successMain;
      } else {
        return reverse
          ? theme.notification.successMain
          : theme.notification.errorMain;
      }
    }

    case TEXT_CONTAINING: {
      if ((rowValue + "").toLowerCase().includes((value + "").toLowerCase())) {
        return reverse
          ? theme.notification.errorMain
          : theme.notification.successMain;
      } else {
        return reverse
          ? theme.notification.successMain
          : theme.notification.errorMain;
      }
    }

    case TEXT_EQUAL_TO: {
      if (rowValue === value) {
        return reverse
          ? theme.notification.errorMain
          : theme.notification.successMain;
      } else {
        return reverse
          ? theme.notification.successMain
          : theme.notification.errorMain;
      }
    }

    case TEXT_NOT_CONTAINING: {
      if (!(rowValue + "").toLowerCase().includes((value + "").toLowerCase())) {
        return theme.notification.successMain;
      } else {
        return theme.notification.errorMain;
      }
    }

    case TEXT_NOT_EQUAL_TO: {
      if (rowValue !== value) {
        return theme.notification.successMain;
      } else {
        return theme.notification.errorMain;
      }
    }

    default:
      return;
  }
}
